import { useAnalytics } from "app/hooks/shared/analytics/useAnalytics";
import {
  newLayoutDoors,
  useConfigurationOptionsContext,
} from "app/providers/DoorOptionsProvider";
import { applyHeightToEigenwijs } from "app/providers/eigenwijsHeight";
import { resolveHandleForLayoutTransition } from "app/providers/handlePolicy";
import { requiresEqualWidths } from "entities/configurator/model/widthSymmetry";
import { newLayoutMinWidth } from "entities/configurator/model/widthLimits";
import { minHeightDeMeent } from "app/providers/ZelfMontageHelper";
import { defaultDoorWidth } from "shared/utils/doorSceneConstants";
import { enabledXLXXLDeMaasDeWaalNewDoors } from "features/option-controls/door-width-controls/XLXXLHelper";
import { useSearchParams } from "react-router-dom";
import { OptionTemplate } from "shared/api/middleware-api-client";
import { removeTrailingNumber } from "app/providers/LayoutsHelper";
import {
  isSameLayoutBrowseGroup,
  isNewLayoutCode,
  isDeMeentCode,
  newLayoutMaxHeight,
  newLayoutMinHeight,
} from "entities/configurator/model/layoutMeta";

export function useLayoutSelector() {
  const { onConfig } = useAnalytics();
  const {
    updateLayout,
    openLayoutGroup,
    groupedLayouts,
    getLayoutSubchoiceCount,
    getConfigurationProduct,
    sortedLayoutList,
    selectedDoor,
    layout: currentLayout,
    eigenwijs,
    updateEigenwijs,
    doorOptions,
    updateHandle,
    handle,
    getConfigurationHeight,
    deMeentHeightChangeApplies,
    mainDeWaal,
    dewaalVariants,
    mainDeMaas,
    deMaasVariants,
    isOpdekOrStomp,
    isSingleMaxHeightDoor,
    maxDoorHeight,
  } = useConfigurationOptionsContext();

  const hasNewLayoutsOption =
    selectedDoor && newLayoutDoors.includes(selectedDoor?.code);

  const [searchParams] = useSearchParams();

  const isCurrentLayoutSpecialLayout = isNewLayoutCode(currentLayout?.code);

  const doorHeight = getConfigurationHeight(0);
  const getAdjustedHeightForLayout = (
    nextLayout: OptionTemplate,
  ): number | undefined => {
    // --- Demeent correction ---
    const isSwitchingToDeMeent =
      deMeentHeightChangeApplies &&
      !isDeMeentCode(currentLayout?.code) &&
      isDeMeentCode(nextLayout.code);

    if (isSwitchingToDeMeent && doorHeight <= minHeightDeMeent) {
      return minHeightDeMeent;
    }

    // --- 2. New layouts min/max ---
    const isNewLayout = isNewLayoutCode(nextLayout.code);

    if (isNewLayout) {
      const maxHeight = isSingleMaxHeightDoor
        ? newLayoutMaxHeight
        : maxDoorHeight;
      const outOfRange =
        doorHeight < newLayoutMinHeight || doorHeight > maxHeight;

      if (outOfRange) {
        return newLayoutMinHeight;
      }
    }
  };

  const onChangeLayout = (layoutToAdd: OptionTemplate) => {
    const isReopeningSelectedGroup =
      groupedLayouts === null &&
      currentLayout &&
      isSameLayoutBrowseGroup(currentLayout.code, layoutToAdd.code);

    if (isReopeningSelectedGroup) {
      openLayoutGroup(currentLayout.code);
      return;
    }

    let layout = { ...layoutToAdd };

    // Handle DeWaal/DeMaas variants for opdek/stomp doors
    if (isOpdekOrStomp) {
      // Check if the current layout is a main variant
      const variantRules = [
        { main: mainDeWaal, variants: dewaalVariants },
        { main: mainDeMaas, variants: deMaasVariants },
      ];

      // Find a matching rule for the current layout
      const match = variantRules.find(
        ({ main, variants }) =>
          main && variants.length > 0 && main.code === layout.code,
      );

      // If a match is found, set the layout code to the first variant's code
      if (match) layout = { ...match.variants[0] };
    }
    const isCurrentLayout = layout.code === currentLayout?.code;

    if (isCurrentLayout) {
      const isGroupOpen = groupedLayouts !== null;

      if (!isGroupOpen) {
        openLayoutGroup(layout.code);
      }

      return;
    }

    updateLayout(layout);
    openLayoutGroup(layout.code);
    if (!selectedDoor) return;

    const adjustedHeight = getAdjustedHeightForLayout(layout);
    const isTheSelectedSpecialLayout = isNewLayoutCode(layout.code);
    const isEnteringNewLayout =
      !isCurrentLayoutSpecialLayout && isTheSelectedSpecialLayout;
    const equalWidthsRequired = requiresEqualWidths(
      selectedDoor.code,
      layout.code,
    );
    const shouldApplyWidthLimits = isEnteringNewLayout && !equalWidthsRequired;
    if (
      eigenwijs &&
      (adjustedHeight !== undefined ||
        shouldApplyWidthLimits ||
        equalWidthsRequired)
    ) {
      let nextEigenwijs =
        adjustedHeight !== undefined
          ? applyHeightToEigenwijs(eigenwijs, adjustedHeight, layout.code)
          : eigenwijs;

      if (shouldApplyWidthLimits) {
        const maxWidth = enabledXLXXLDeMaasDeWaalNewDoors.includes(
          selectedDoor.code,
        )
          ? 1750
          : 1150;
        nextEigenwijs = {
          ...nextEigenwijs,
          sizeObjects: nextEigenwijs.sizeObjects.map((sizeObject) => ({
            ...sizeObject,
            customWidth:
              sizeObject.customWidth > maxWidth ||
              sizeObject.customWidth < newLayoutMinWidth
                ? defaultDoorWidth
                : sizeObject.customWidth,
          })),
        };
      }

      updateEigenwijs(nextEigenwijs, layout.code);
    }

    const layoutDrivenHandle = resolveHandleForLayoutTransition({
      doorCode: selectedDoor.code,
      currentLayoutCode: currentLayout?.code,
      nextLayoutCode: layout.code,
      currentHandle: handle,
      handleOptions: doorOptions?.handle,
    });
    if (layoutDrivenHandle && layoutDrivenHandle.code !== handle?.code) {
      updateHandle(layoutDrivenHandle);
    }

    const product = getConfigurationProduct();
    if (product)
      onConfig({
        ...product,
        doorLayout: {
          id: layout.id,
          name: layout.name,
          code: layout.code,
          description: layout.description,
          imageId: layout.imageId,
        },
      });
  };

  const getDisplayName = (layout: OptionTemplate) => {
    return layout.code === mainDeWaal?.code
      ? removeTrailingNumber(layout.name)
      : layout.name;
  };

  return {
    hasNewLayoutsOption,
    sortedLayoutList,
    getLayoutSubchoiceCount,
    onChangeLayout,
    searchParams,
    getDisplayName,
  };
}
